/**
 * DESIGN TOKENS — Single Source of Truth
 *
 * All color tokens for the Meester djay design system.
 * Components MUST import from here instead of hardcoding Tailwind color classes.
 *
 * To change the brand color: update ONLY this file + index.css CSS variables.
 *
 * Architecture: DDD-aligned. This is a cross-cutting config concern.
 * Location: src/config/designTokens.ts (alongside constants.ts)
 */

// ─── Brand / Primary ────────────────────────────────────────────────
export const PRIMARY = {
  bg: 'bg-orange-500',
  bgHover: 'hover:bg-orange-400',
  bgLight: 'bg-orange-50',
  bgLightHover: 'hover:bg-orange-100',
  bgMuted: 'bg-orange-50/80',
  text: 'text-orange-600',
  textDark: 'text-orange-700',
  textDarker: 'text-orange-900',
  textOnPrimary: 'text-white',
  border: 'border-orange-200',
  borderStrong: 'border-orange-400',
  borderHover: 'hover:border-orange-300',
  ring: 'ring-orange-300',
  ringLight: 'ring-orange-100',
  focusRing: 'focus:ring-2 focus:ring-orange-300',
  focusBorder: 'focus:border-orange-400',
  shadow3d: 'shadow-[0_4px_0_0_rgb(194,65,12)]',
  shadow3dHover: 'hover:shadow-[0_4px_0_0_rgb(194,65,12)]',
  shadowGlow: 'shadow-orange-200/50',
  gradient: 'from-orange-500 via-amber-500 to-yellow-500',
  gradientSubtle: 'from-orange-50 to-amber-50'
} as const;

// ─── Outline variant (secondary brand) ─────────────────────────────
export const OUTLINE = {
  bg: 'bg-transparent',
  bgHover: 'hover:bg-orange-50',
  text: 'text-orange-600',
  border: 'border-orange-200',
  borderHover: 'hover:border-orange-300',
  bgActive: 'active:bg-orange-100',
  ring: 'ring-orange-300'
} as const;

// ─── Success (correct answers, mastered) ────────────────────────────
export const SUCCESS = {
  bg: 'bg-emerald-500',
  bgLight: 'bg-emerald-50',
  text: 'text-emerald-600',
  textDark: 'text-emerald-700',
  border: 'border-emerald-200',
  ring: 'ring-emerald-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(5,150,105)]'
} as const;

// ─── Danger / Warning (used for "danger" button variant) ────────────
export const DANGER = {
  bg: 'bg-amber-500',
  bgHover: 'hover:bg-amber-400',
  text: 'text-amber-600',
  border: 'border-amber-200',
  ring: 'ring-amber-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(217,119,6)]'
} as const;

// ─── XP / Gamification ─────────────────────────────────────────────
export const XP = {
  bg: 'bg-purple-500',
  bgLight: 'bg-purple-50',
  text: 'text-purple-600',
  textDark: 'text-purple-700',
  border: 'border-purple-200'
} as const;

// ─── Neutral / Ghost ────────────────────────────────────────────────
export const NEUTRAL = {
  bg: 'bg-white',
  bgHover: 'hover:bg-slate-50',
  bgMuted: 'bg-slate-50',
  text: 'text-slate-600',
  textDark: 'text-slate-700',
  textDarker: 'text-slate-900',
  border: 'border-slate-200',
  borderHover: 'hover:border-slate-300',
  ring: 'ring-slate-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(226,232,240)]'
} as const;

// ─── Role-Based Colors ─────────────────────────────────────────────
// Teacher uses indigo (intentional brand differentiation)
// Parent uses emerald
// Learner uses primary (orange)
export const ROLE_COLORS = {
  teacher: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-700',
    hoverBorder: 'hover:border-indigo-300'
  },
  parent: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
    hoverBorder: 'hover:border-emerald-300'
  },
  learner: {
    bg: PRIMARY.bgLight,
    text: PRIMARY.textDark,
    border: PRIMARY.border,
    iconBg: 'bg-orange-100',
    iconText: PRIMARY.text,
    badgeBg: 'bg-orange-100',
    badgeText: PRIMARY.textDark,
    hoverBorder: PRIMARY.borderHover
  }
} as const;

// ─── Badge Variants ─────────────────────────────────────────────────
export const BADGE_VARIANTS = {
  default: 'bg-slate-100 text-slate-700 border-slate-200',
  primary: `bg-orange-100 text-orange-700 border-orange-200`,
  secondary: 'bg-amber-100 text-amber-700 border-amber-200',
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger: 'bg-rose-100 text-rose-700 border-rose-200',
  info: 'bg-blue-100 text-blue-700 border-blue-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  outline: 'bg-transparent text-slate-700 border-slate-300'
} as const;

// ─── Section Accent (default for SectionHeader) ────────────────────
export const SECTION_ACCENT = {
  default: `bg-orange-100 text-orange-600`
} as const;

// ─── Card ───────────────────────────────────────────────────────────
export const CARD = {
  base: 'bg-white rounded-3xl border border-slate-200 shadow-sm',
  hoverBorder: 'rgb(254 215 170)' // orange-200 for framer-motion borderColor
} as const;

// ─── Input (student variant) ────────────────────────────────────────
export const INPUT_STUDENT = {
  base: 'border-2 border-slate-200 rounded-2xl bg-slate-50/80 focus:bg-white text-slate-900 placeholder:text-slate-400',
  focus: 'focus:ring-2 focus:ring-orange-100 focus:border-orange-400',
  error: 'border-red-300 focus:ring-red-500 focus:border-red-500'
} as const;

// ─── Progress defaults ──────────────────────────────────────────────
export const PROGRESS = {
  barColor: 'bg-orange-500',
  ringColor: 'text-orange-500'
} as const;

// ─── Sidebar / Navigation active state ──────────────────────────────
export const NAV_ACTIVE_DEFAULT = {
  text: 'text-orange-600',
  bg: 'bg-orange-50',
  border: 'border-orange-200',
  icon: 'text-orange-600'
} as const;